import { useState, type CSSProperties } from "react";
import type { AiChatResult } from "../../scenario/types";
import { AI_CATEGORY_LABEL } from "../../scenario/types";
import { Ico, type IcoName } from "./icons";
import { actionLabel, CATEGORY_WHY, type FileReviewRow, type ReviewData, type ShareMethod, type Verdict } from "./review";

/**
 * End-of-episode report for the AI assistant. A short paged report rather
 * than one long scroll:
 *   1. Overview        what happened, and how many of the choices were safe
 *   2. Your choices    every decision next to the safe choice
 *   3. Lines to hide    every line that should be blacked out, and why
 *   4. What left       what went out, why it matters, what to do next time
 * Every learner passes through all of them; the tab strip lets them jump.
 */

const TIER_META: Record<AiChatResult["tier"], { label: string; tone: "bad" | "warn" | "ok"; head: string }> = {
  critical: { label: "Critical failure", tone: "bad", head: "This should not have left the company" },
  high: { label: "High risk", tone: "bad", head: "Too much left the company" },
  partial: { label: "Partially correct", tone: "warn", head: "Close, but more left than needed" },
  best: { label: "Best decision", tone: "ok", head: "Handled well" },
  safe_alt: { label: "Safe, you paused", tone: "ok", head: "You asked before acting" },
};

const SHARE_NAME: Record<ShareMethod, string> = {
  public_link: "Public share link",
  copy_text: "Pasted the summary text",
  internal_channel: "Internal reply",
  guidance: "Paused to ask first",
};

const VERDICT_META: Record<Verdict, { label: string; icon: IcoName }> = {
  match: { label: "Safe choice", icon: "check" },
  ok: { label: "Also safe", icon: "check" },
  differs: { label: "Not safe", icon: "warn" },
};

function ResChip({ verdict }: { verdict: Verdict }) {
  const m = VERDICT_META[verdict];
  return (
    <span className={`cgx-res is-${verdict}`}>
      <Ico name={m.icon} /> {m.label}
    </span>
  );
}

function worstOf(verdicts: Verdict[]): Verdict {
  if (verdicts.includes("differs")) return "differs";
  if (verdicts.includes("ok")) return "ok";
  return "match";
}

function Tile({ icon, label, line, verdict }: { icon: IcoName; label: string; line: string; verdict: Verdict }) {
  return (
    <div className={`cgx-tile is-${verdict}`}>
      <span className="cgx-tile-ico">
        <Ico name={icon} />
      </span>
      <div className="cgx-tile-txt">
        <span className="cgx-tile-label">{label}</span>
        <strong>{line}</strong>
      </div>
      <ResChip verdict={verdict} />
    </div>
  );
}

function ChoiceRow({
  icon,
  badge,
  title,
  safe,
  safeSub,
  safeTitle,
  did,
  verdict,
  note,
}: {
  icon?: IcoName;
  badge?: string;
  title: string;
  safe: string;
  safeSub?: string | null;
  safeTitle?: string;
  did: string;
  verdict: Verdict;
  note: string;
}) {
  return (
    <div className={`cgx-ch-row is-${verdict}`}>
      <div className="cgx-ch-what">
        {badge ? (
          <span className={`cgx-ftype cgx-ftype--${badge.toLowerCase()}`}>{badge}</span>
        ) : (
          <span className="cgx-ch-ico">{icon && <Ico name={icon} />}</span>
        )}
        <strong>{title.replace(/_/g, "_\u200b")}</strong>
      </div>
      <div className="cgx-ch-safe" title={safeTitle}>
        <span className="cgx-ch-k">Safe choice</span>
        <strong>{safe}</strong>
        {safeSub && <span className="cgx-ch-sub">{safeSub}</span>}
      </div>
      <div className="cgx-ch-did">
        <span className="cgx-ch-k">You did</span>
        <strong>{did}</strong>
      </div>
      <ResChip verdict={verdict} />
      <p className="cgx-ch-note">{note}</p>
    </div>
  );
}

function LinesToHide({ row }: { row: FileReviewRow }) {
  const hide = row.lines.filter((l) => l.shouldHide);
  if (hide.length === 0) {
    return (
      <p className="cgx-lines-none">
        Nothing in this file should be blacked out. {row.safe.why}
      </p>
    );
  }
  return (
    <div className="cgx-lines">
      <p className="cgx-lines-k">Lines that should be blacked out</p>
      <ul>
        {hide.map((l) => (
          <li key={l.seg.id}>
            <span>{l.seg.text}</span>
            <em>{l.seg.category ? CATEGORY_WHY[l.seg.category] : "This line is not needed for the summary."}</em>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StateTag({ black }: { black: boolean }) {
  return (
    <span className={`cgx-state ${black ? "is-black" : "is-open"}`}>
      <Ico name={black ? "redact" : "eye"} /> {black ? "Blacked out" : "Readable"}
    </span>
  );
}

function RedactionCard({ row }: { row: FileReviewRow }) {
  const lines = row.lines ?? [];
  const total = row.sensitiveTotal;
  return (
    <article className={`cgx-rd is-${row.verdict}`}>
      <header className="cgx-rd-head">
        <span className={`cgx-ftype cgx-ftype--${row.doc.fileType}`}>{row.doc.fileType.toUpperCase()}</span>
        <div className="cgx-rd-title">
          <strong>{row.doc.name}</strong>
          <span>
            {total === 0
              ? "Nothing in this file should be blacked out."
              : `${total} line${total === 1 ? "" : "s"} should be blacked out before this file is sent.`}
          </span>
        </div>
        <ResChip verdict={row.verdict} />
      </header>
      <div className="cgx-rd-stats">
        <span className="cgx-stat is-ok">
          <Ico name="check" /> {row.correct} blacked out correctly
        </span>
        {row.missed > 0 && (
          <span className="cgx-stat is-bad">
            <Ico name="warn" /> {row.missed} sensitive line{row.missed === 1 ? "" : "s"} left readable
          </span>
        )}
        {row.overNeeded > 0 && (
          <span className="cgx-stat is-warn">
            <Ico name="warn" /> {row.overNeeded} line{row.overNeeded === 1 ? "" : "s"} hidden that the summary needs
          </span>
        )}
        {row.overHarmless > 0 && (
          <span className="cgx-stat is-mute">
            <Ico name="info" /> {row.overHarmless} harmless line{row.overHarmless === 1 ? "" : "s"} hidden
          </span>
        )}
      </div>
        {lines.length === 0 ? (
          <p className="cgx-lines-none">{row.safe.why}</p>
        ) : (
        <div className="cgx-rd-table" role="table" aria-label={`Lines to hide in ${row.doc.name}`}>
        <div className="cgx-rd-th" role="row">
          <span>Line</span>
          <span>You</span>
          <span>Should be</span>
        </div>
        {lines.map((l) => {
          const cat = l.seg.category ? AI_CATEGORY_LABEL[l.seg.category] : "Not sensitive";
          const label = l.status === "over_needed" ? "Needed for the summary" : l.status === "over_harmless" ? "Harmless line" : cat;
          const why = l.shouldHide && l.seg.category
            ? CATEGORY_WHY[l.seg.category]
            : l.status === "over_needed"
            ? "Priya needs this line for the summary, so it should stay readable."
            : l.status === "over_harmless"
            ? "Nothing sensitive here. Hiding it is not needed."
            : l.shouldHide
            ? "This line is not needed for the summary."
            : null;
          return (
            <div key={l.seg.id} className={`cgx-rd-row is-${l.status}`} role="row">
              <div className="cgx-rd-line">
                <span className="cgx-rd-cat">{label}</span>
                <span className="cgx-rd-text">{l.seg.text}</span>
                {why && <span className="cgx-rd-why">{why}</span>}
              </div>
              <StateTag black={l.didHide} />
              <StateTag black={l.shouldHide} />
            </div>
          );
        })}
      </div>
        )}
      <p className="cgx-rd-note">
        <Ico name={row.verdict === "differs" ? "warn" : "check"} /> {row.note}
      </p>
    </article>
  );
}

type PageId = "overview" | "choices" | "redaction" | "left";

export function ResultReport({
  result,
  review,
  onSeeEscape,
  onContinue,
}: {
  result: AiChatResult;
  review: ReviewData;
  onSeeEscape: () => void;
  onContinue: () => void;
}) {
  const meta = TIER_META[result.tier];
  const paused = result.workspaceKind === "none";
  const pages: { id: PageId; label: string }[] = paused
    ? [{ id: "overview", label: "Result" }]
    : [
        { id: "overview", label: "Overview" },
        { id: "choices", label: "Your choices" },
        { id: "redaction", label: "Lines to hide" },
        { id: "left", label: "What left" },
      ];
  const [pageIdx, setPageIdx] = useState(0);
  const page = pages[Math.min(pageIdx, pages.length - 1)].id;
  const last = pageIdx >= pages.length - 1;

  const pct = Math.round((review.safeCount / Math.max(1, review.total)) * 100);
  const fileVerdict = worstOf(review.files.map((f) => f.verdict));
  const badFiles = review.files.filter((f) => f.verdict === "differs").length;
  const sentCount = review.files.filter((f) => f.action !== "skip").length;
  const sendBlurb =
    sentCount === 0
      ? "Nothing was attached"
      : `${sentCount} of ${review.files.length} files sent${badFiles ? `, ${badFiles} not safe` : ", all handled safely"}`;

  return (
    <div className="winapp cgpt-browser cgx-root">
      <div className="cgpt-chrome">
        <span className="cgpt-dot cgpt-dot--r" aria-hidden />
        <span className="cgpt-dot cgpt-dot--y" aria-hidden />
        <span className="cgpt-dot cgpt-dot--g" aria-hidden />
        <div className="cgpt-omni" aria-hidden>
          <span className="cgpt-omni-url">Your results: what left the company</span>
        </div>
      </div>

      <div className={`cgx cgx-report cgx-report--${meta.tone}`}>
        {pages.length > 1 && (
          <nav className="cgx-tabs" aria-label="Report pages">
            {pages.map((pg, i) => (
              <button
                key={pg.id}
                type="button"
                className={`cgx-tab ${i === pageIdx ? "is-on" : ""} ${i < pageIdx ? "is-seen" : ""}`}
                aria-current={i === pageIdx ? "page" : undefined}
                onClick={() => setPageIdx(i)}
              >
                <span className="cgx-tab-n">{i + 1}</span>
                {pg.label}
              </button>
            ))}
          </nav>
        )}

        <div className="cgx-report-scroll" key={page}>
          {page === "overview" && (
            <div className="cgx-ov">
              <div className={`cgx-hero cgx-hero--${meta.tone}`}>
                <span className="cgx-hero-ico">
                  <Ico name={meta.tone === "ok" ? "shield" : "warn"} />
                </span>
                <div className="cgx-hero-txt">
                  <span className="cgx-badge">{meta.label}</span>
                  <h2>{meta.head}</h2>
                  <p>{result.summaryLine}</p>
                </div>
                {!paused && (
                  <div className="cgx-score" style={{ "--p": pct } as CSSProperties}>
                    <div className="cgx-score-ring">
                      <strong>
                        {review.safeCount}/{review.total}
                      </strong>
                    </div>
                    <span>choices were safe</span>
                  </div>
                )}
              </div>

              {paused ? (
                <div className="cgx-rec">
                  <span className="cgx-rec-tag">Safer next time</span>
                  <p>{result.recommendation}</p>
                </div>
              ) : (
                <>
                  <div className="cgx-tiles">
                    <Tile icon="user" label="Account" line={review.account.chosen.label} verdict={review.account.verdict} />
                    <Tile icon="file" label="Files" line={sendBlurb} verdict={fileVerdict} />
                    <Tile
                      icon="share"
                      label="Sharing"
                      line={review.share ? SHARE_NAME[review.share.method] : "Not shared"}
                      verdict={review.share?.verdict ?? "match"}
                    />
                  </div>
                  {result.goodCalls.length > 0 && (
                    <p className="cgx-good">
                      <Ico name="check" /> {result.goodCalls[0]}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {page === "choices" && (
            <div className="cgx-ch">
              <h3 className="cgx-h3">Your choices next to the safe choices</h3>
              <ChoiceRow
                icon="user"
                title="Which account"
                safe={review.account.safeLabel}
                safeSub="Managed by the company"
                did={review.account.chosen.label}
                verdict={review.account.verdict}
                note={review.account.note}
              />
              {review.files.map((f) => (
                <div key={f.doc.id} className="cgx-file-block">
                  <ChoiceRow
                    badge={f.doc.fileType.toUpperCase()}
                    title={f.doc.name}
                    safe={f.safe.label}
                    safeSub={f.safe.alt}
                    safeTitle={f.safe.why}
                    did={actionLabel(f.action, f.hiddenCount)}
                    verdict={f.verdict}
                    note={f.note}
                  />
                  <LinesToHide row={f} />
                </div>
              ))}
              {review.share && (
                <ChoiceRow
                  icon="share"
                  title="Sharing the summary"
                  safe={review.share.safeLabel}
                  safeSub="Only the words leave the chat"
                  did={SHARE_NAME[review.share.method]}
                  verdict={review.share.verdict}
                  note={review.share.note}
                />
              )}
            </div>
          )}

          {page === "redaction" && (
            <div className="cgx-rds">
              <h3 className="cgx-h3">Every line that should be blacked out, and why</h3>
              {review.files.map((row) => (
                <RedactionCard key={row.doc.id} row={row} />
              ))}
            </div>
          )}

          {page === "left" && (
            <div className="cgx-left">
              <section className="cgx-card">
                <h4>Information that left the company</h4>
                {result.disclosed.length ? (
                  <ul className="cgx-disc">
                    {result.disclosed.map((d) => (
                      <li key={d.category} className={d.necessary ? "is-ok" : "is-bad"}>
                        <span className="cgx-disc-dot" aria-hidden>
                          <Ico name={d.necessary ? "check" : "close"} />
                        </span>
                        <span className="cgx-disc-cat">{AI_CATEGORY_LABEL[d.category]}</span>
                        <span className="cgx-disc-need">{d.necessary ? "Needed for the task" : "Not needed"}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="cgx-none">Nothing confidential left the company.</p>
                )}
              </section>

              {(result.concerns.length > 0 || result.goodCalls.length > 0) && (
                <section className={`cgx-card ${result.concerns.length ? "cgx-card--bad" : "cgx-card--ok"}`}>
                  {result.goodCalls.length > 0 && (
                    <>
                      <h4>What you got right</h4>
                      <ul className="cgx-list is-ok">
                        {result.goodCalls.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {result.concerns.length > 0 && (
                    <>
                      <h4>Why this matters</h4>
                      <ul className="cgx-list">
                        {result.concerns.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </section>
              )}

              <div className="cgx-rec cgx-rec--wide">
                <span className="cgx-rec-tag">Safer next time</span>
                <p>{result.recommendation}</p>
              </div>

              <button
                type="button"
                className={`cgx-escape ${result.sharedPublicLink ? "cgx-btn--blink" : ""}`}
                onClick={onSeeEscape}
              >
                <Ico name="link" />
                {result.sharedPublicLink ? "See where that public link can end up" : "See what a public link could have exposed"}
                <Ico name="chevright" />
              </button>
            </div>
          )}
        </div>

        <footer className="cgx-report-foot">
          {pageIdx > 0 ? (
            <button type="button" className="cgx-btn cgx-btn--ghost" onClick={() => setPageIdx((i) => Math.max(0, i - 1))}>
              <Ico name="back" /> Back
            </button>
          ) : (
            <span />
          )}
          <span className="cgx-report-count">
            {pages.length > 1 ? `Page ${pageIdx + 1} of ${pages.length}` : ""}
          </span>
          {last ? (
            <button type="button" className="cgx-btn cgx-btn--primary" onClick={onContinue}>
              Continue
            </button>
          ) : (
            <button type="button" className="cgx-btn cgx-btn--primary" onClick={() => setPageIdx((i) => Math.min(pages.length - 1, i + 1))}>
              Next: {pages[pageIdx + 1].label} <Ico name="forward" />
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
